import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUGGESTIONS = [
  "Cosa mi consigli stasera?",
  "Quali sono gli orari?",
  "Quanto costa il risotto in cagnone?",
];

const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([
    { role: "assistant", text: "Buonasera! Sono l'assistente del Baracca. Mi chieda pure del menù, degli orari o dei nostri piatti tipici." },
  ]);
  const [loading, setLoading] = useState(false);
  const sessionId = useRef(`baracca-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
  const listRef = useRef(null);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: msg }, { role: "assistant", text: "" }]);
    setLoading(true);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, message: msg }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data: ") || part === "data: [DONE]") continue;
          const payload = JSON.parse(part.slice(6));
          const chunk = payload.delta ?? payload.error ?? "";
          setMessages((m) => {
            const copy = [...m];
            copy[copy.length - 1] = { role: "assistant", text: copy[copy.length - 1].text + chunk };
            return copy;
          });
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", text: "Connessione non riuscita. Ci chiami pure allo 015 21941." };
        return copy;
      });
    }
    setLoading(false);
  };

  return (
    <>
      <motion.button
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 1.8, duration: 0.5 }}
        onClick={() => setOpen((o) => !o)}
        data-testid="chat-open-button"
        aria-label={open ? "Chiudi assistente" : "Apri assistente"}
        className="fixed bottom-6 right-6 z-[95] flex h-14 w-14 items-center justify-center rounded-full bg-[#1C1917] text-[#FAF8F5] shadow-[0_12px_40px_rgba(28,25,23,0.35)] hover:bg-[#7A1C28] transition-colors duration-300"
      >
        {open ? <X size={20} /> : <MessageCircle size={20} />}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-24 right-4 sm:right-6 z-[95] w-[calc(100vw-2rem)] max-w-sm overflow-hidden rounded-2xl border border-[#E7E4DF] bg-[#FAF8F5] shadow-[0_24px_80px_rgba(28,25,23,0.25)]"
            data-testid="chat-panel"
          >
            <div className="bg-[#1C1917] px-5 py-4">
              <p className="font-serif-display text-lg text-[#FAF8F5]">L'assistente del Baracca</p>
              <p className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-[#D99B26]">Menù · Orari · Prenotazioni</p>
            </div>

            <div ref={listRef} className="h-80 overflow-y-auto px-4 py-4 space-y-3" data-testid="chat-message-list">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                      m.role === "user"
                        ? "bg-[#1C1917] text-[#FAF8F5] rounded-br-sm"
                        : "bg-[#F4E8C1]/60 text-[#1C1917] rounded-bl-sm"
                    }`}
                  >
                    {m.text || (loading && i === messages.length - 1 ? <span className="animate-pulse">…</span> : null)}
                  </div>
                </div>
              ))}
            </div>

            {messages.length <= 1 && (
              <div className="flex flex-wrap gap-2 px-4 pb-3">
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    onClick={() => send(s)}
                    data-testid={`chat-suggestion-${SUGGESTIONS.indexOf(s)}`}
                    className="rounded-full border border-[#1C1917]/15 px-3 py-1.5 text-xs text-[#6E6A66] hover:border-[#1C1917] hover:text-[#1C1917] transition-colors"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}

            <form
              onSubmit={(e) => { e.preventDefault(); send(); }}
              className="flex items-center gap-2 border-t border-[#E7E4DF] px-3 py-3"
            >
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Scrivi una domanda…"
                data-testid="chat-input"
                className="flex-1 bg-transparent px-2 py-2 text-sm outline-none placeholder:text-[#6E6A66]/60"
              />
              <button
                type="submit"
                disabled={loading || !input.trim()}
                data-testid="chat-send-button"
                aria-label="Invia messaggio"
                className="flex h-10 w-10 items-center justify-center rounded-full bg-[#1C1917] text-[#FAF8F5] disabled:opacity-40 hover:bg-[#7A1C28] transition-colors"
              >
                <Send size={15} />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ChatWidget;
